import React, {useEffect, useState} from "react";
import {removeDepreciations, updateDepreciation} from "../../../../../api/asset/AssetDepreciationService";
import Th_title from "../../../../../components/Th_title";
import {depreciationItem, depreciationType, depreciationYear} from "../AssetDepreciationCreate";
import {SelectComboBoxForUpdate} from "../../../../../components/select/SelectComboBoxForUpdate";
import {formatNumber} from "../../../../../components/commonmethod/CommonMethod";
import {
    DetailEntityShell,
    DetailKvTable,
    DetailPanel,
    DetailRollupActions,
    useDetailEditing,
} from "../../../../../components/detailLayout";
import {inputOnChange, inputOnKeyDown} from "../../../../../components/commonmethod/simmsInputMethod";
import SimmsInput from "../../../../../components/input/SimmsInput";
import {dateOnChange, dateOnSelected} from "../../../../../components/commonmethod/simmsDateMethod";
import SimmsDatePicker from "../../../../../components/datepicker/SimmsDatePicker";
import {useNavigate} from "react-router-dom";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../../../components/pageTabs/pageTabsStorage";
import DepreciationCalcTable from "../components/DepreciationCalcTable";

function buildDepreciationMeta(depreciation) {
    return [
        depreciation.item && `항목 ${depreciation.item}`,
        depreciation.type && `방법 ${depreciation.type}`,
    ]
        .filter(Boolean)
        .join(" · ");
}

function AssetDepreciationInfo({depreciation, setDepreciation}) {
    const navigate = useNavigate();
    if (!depreciation) return null;

    const remove = async () => {
        const response = await removeDepreciations({ids: [depreciation.id]});
        if (response?.status === 200) {
            alert("감가상각이 정상적으로 삭제되었습니다.");
            navigate("/asset/depreciation");
        }
    };

    return (
        <DetailEntityShell
            title={depreciation.asset?.assetName || "감가상각상세"}
            meta={buildDepreciationMeta(depreciation)}
            canUpdate={depreciation.auth?.update}
            listUrl="/asset/depreciation"
            rollupActions={depreciation.auth?.delete ? <DetailRollupActions remove={remove} /> : null}
        >
            <AssetDepreciationInfoBody depreciation={depreciation} setDepreciation={setDepreciation} />
        </DetailEntityShell>
    );
}

function AssetDepreciationInfoBody({depreciation, setDepreciation}) {
    const [calc, setCalc] = useState([]);
    const isEditing = useDetailEditing();
    const fieldAuth = isEditing && depreciation.auth?.update;

    // 의존성 변경 시 계산 함수 실행
    useEffect(() => {
        if (depreciation?.type && depreciation?.year && depreciation?.price && depreciation?.rate && depreciation?.depreciationDate) {
            const newCalc = calcAndReturnList(depreciation);
            setCalc(newCalc); // 계산 결과 상태 업데이트
        }
    }, [depreciation]);

    const validateDepreciation = () => {
        let isValid = true;

        if (depreciation.type === "정률법" && depreciation.rate > 100) {
            alert("감가상각률은 100%를 넘을 수 없습니다. \n감가상각률 또는 계산방법을 변경해주세요");
            setDepreciation((prev) => ({
                ...prev,
                rate: null,
                required_validate: { ...prev.required_validate, rate: false },
            }));
            isValid = false;
        } else if (depreciation.type === "정액법" && parseInt(depreciation.rate) > parseInt(depreciation.price)) {
            alert(`잔존가치는 취득가액을 넘을 수 없습니다. \n잔존가치 또는 계산방법을 변경해주세요. \n잔존가치 : ${depreciation.rate} \n취득가액 : ${depreciation.price}`);
            setDepreciation((prev) => ({
                ...prev,
                rate: null,
                required_validate: { ...prev.required_validate, rate: false },
            }));
            isValid = false;
        }
        return isValid;
    };

    const calcAndReturnList = (depreciation) => {
        const list = [];
        let price = depreciation.price;
        let sum = 0;

        const isValid = validateDepreciation();
        if (isValid) {
            for (let i = 0; i < parseInt(depreciation.year); i++) {
                const dateText = `${parseInt(depreciation.depreciationDate.split("-")[0]) + i}-${depreciation.depreciationDate.split("-")[1]}-${depreciation.depreciationDate.split("-")[2]}`;

                if (depreciation.type === "정률법") {
                    const ll = Math.floor(price * depreciation.rate / 100);
                    price -= ll;
                    sum += ll;

                    list.push({
                        year: dateText,
                        annual: `${i + 1}년차`,
                        amount: formatNumber(ll),
                        accumulated: formatNumber(sum),
                        remain: formatNumber(price),
                    });
                } else if (depreciation.type === "정액법") {
                    const ll = Math.floor((depreciation.price - depreciation.rate) / parseInt(depreciation.year));
                    sum += ll;

                    list.push({
                        year: dateText,
                        annual: `${i + 1}년차`,
                        amount: formatNumber(ll),
                        accumulated: formatNumber(sum),
                        remain: formatNumber(depreciation.price - sum),
                    });
                }
            }
        }
        return list;
    };

    async function handleUpdate(v){
        const isValid = validateDepreciation();
        if (isValid) {
            return await updateDepreciation(v);
        } else {
            return false;
        }
    }

    const navigate = useNavigate();

    function moveHandler() {
        const asset = depreciation?.asset;
        if (asset?.id == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(asset, "자산상세"),
            url: `/asset/${asset.id}`,
            headerUrl: "/asset/list",
            headerTitle: "자산정보관리",
        });
    }

    return (
        <>
                <DetailPanel title="기본정보">
                    <DetailKvTable>
                        <tbody>
                        <tr>
                            <Th_title title={"자산명"} colspan={3} className="fieldset-td">
                                <div className="inline-flex max-w-full min-h-[28px] items-center gap-2">
                                    <span className="asset-detail-kv-value truncate">
                                        {depreciation.asset.assetName}
                                    </span>
                                    <button
                                        type="button"
                                        className="user-selection-chips-view-all shrink-0"
                                        onClick={moveHandler}
                                    >
                                        바로가기
                                    </button>
                                </div>
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"자산항목"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                {isEditing ? (
                                    <SelectComboBoxForUpdate target={"item"} id={depreciation.id} options={depreciationItem} fnc={setDepreciation} defaultValue={depreciation.item} listener={updateDepreciation} />
                                ) : (
                                    <span className="asset-detail-kv-value">{depreciation.item}</span>
                                )}
                            </Th_title>
                            <Th_title title={"계산방법"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                {isEditing ? (
                                    <SelectComboBoxForUpdate target={"type"} id={depreciation.id} options={depreciationType} fnc={setDepreciation} defaultValue={depreciation.type} listener={(v) => handleUpdate(v)} />
                                ) : (
                                    <span className="asset-detail-kv-value">{depreciation.type}</span>
                                )}
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"내용연수"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                {isEditing ? (
                                    <SelectComboBoxForUpdate target={"year"} id={depreciation.id} options={depreciationYear} fnc={setDepreciation} defaultValue={depreciation.year.toString()} listener={(v) => handleUpdate(v)} />
                                ) : (
                                    <span className="asset-detail-kv-value">{depreciation.year}</span>
                                )}
                            </Th_title>
                            <Th_title title={depreciation.type && depreciationType.find(item => item.name === depreciation.type).text} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                <SimmsInput onChange={(v)=>inputOnChange(setDepreciation,"rate", v, true)}
                                            update onKeyDown={(v, e)=>inputOnKeyDown(setDepreciation, (v) => handleUpdate(v), depreciation.id, "rate", v, e, true)}
                                            data={depreciation.rate} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"취득 일자"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                <SimmsDatePicker onChange={(v)=>dateOnChange(setDepreciation,"depreciationDate", v, true)}
                                                 update onUpdate={(v)=>dateOnSelected(setDepreciation, (v) => handleUpdate(v), depreciation.id, "depreciationDate", v, true)}
                                                 data={depreciation.depreciationDate} />
                            </Th_title>
                            <Th_title title={"취득가액(단위 : 원)"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                <SimmsInput onChange={(v)=>inputOnChange(setDepreciation,"price", v, true)}
                                            update onKeyDown={(v, e)=>inputOnKeyDown(setDepreciation, (v) => handleUpdate(v), depreciation.id, "price", v, e, true)}
                                            data={depreciation.price} />
                            </Th_title>
                        </tr>
                        </tbody>
                    </DetailKvTable>
                </DetailPanel>

                <DetailPanel title="계산">
                    <DepreciationCalcTable rows={calc} />
                </DetailPanel>
        </>
    );
}

export {AssetDepreciationInfo}